import { createAsyncThunk } from "@reduxjs/toolkit";
import { del, get, patch } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import {
  buildGlobalWorkItemsAllQuery,
  buildMyWorkItemsAllQuery,
} from "./projectManagementThunkUtils";
import {
  mapLogPickerWorkItemsResponse,
  mapMyWorkItemsAllResponse,
} from "../../Pages/TimeEntry/timeEntryMappers";

function buildTeamOrProjectSummaryParams(params = {}) {
  const finalParams = { ...params };
  if (finalParams.startDate && !finalParams.dateFrom) {
    finalParams.dateFrom = finalParams.startDate;
  }
  if (finalParams.endDate && !finalParams.dateTo) {
    finalParams.dateTo = finalParams.endDate;
  }
  return finalParams;
}

export const fetchTimeEntries = createAsyncThunk(
  "timeEntries/fetchData",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.TIME_ENTRIES.GET_MY_TIME_LOGS, params);
      return response?.data ?? response;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch time entries"));
    }
  }
);

/** GET /time-logs — paginated list with client/project/team/user/date filters. */
export const fetchAllTimeEntries = createAsyncThunk(
  "timeEntries/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.TIME_ENTRIES.GET_ALL_TIME_LOGS, params);
      return response?.data ?? response;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch time entries"));
    }
  }
);

/** GET /work-items/my/all — unpaginated id + name; same filter params as GET /work-items/my (no page/limit). */
export const fetchMyWorkItemsAll = createAsyncThunk(
  "timeEntries/fetchMyWorkItemsAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.WORK_ITEMS.GET_MY_ALL,
        buildMyWorkItemsAllQuery(params),
        {},
        { requestKey: "time-entry:work-items-my-all" }
      );
      return mapMyWorkItemsAllResponse(response?.data ?? response);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch work items"));
    }
  }
);

/**
 * GET /work-items/all — unpaginated global work items for Log Time modal issue picker (all items the user may log time on, not GET /work-items/my/all).
 * Uses the same filter keys as Time Entry task filters where applicable (`projectId`, `clientId`, `teamIds`); no page/limit.
 */
export const fetchAllWorkItemsForLogPicker = createAsyncThunk(
  "timeEntries/fetchAllWorkItemsForLogPicker",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.WORK_ITEMS.GET_ALL_GLOBAL_KANBAN,
        buildGlobalWorkItemsAllQuery(params),
        {},
        { requestKey: "time-entry:work-items-all-for-log-picker" }
      );
      const list = mapLogPickerWorkItemsResponse(response?.data ?? response);
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch work items"));
    }
  }
);

export const updateMyTimeLog = createAsyncThunk(
  "timeEntries/updateMyTimeLog",
  async ({ id, minutesLogged, logDate, comment, billableMinutes }, { rejectWithValue }) => {
    try {
      const body = {};
      if (minutesLogged != null) body.minutesLogged = Number(minutesLogged);
      if (logDate != null) body.logDate = logDate;
      if (comment !== undefined) body.comment = comment;
      if (billableMinutes != null && Number.isFinite(Number(billableMinutes))) {
        body.billableMinutes = Number(billableMinutes);
      }

      const response = await patch(
        API_ENDPOINTS.TIME_ENTRIES.UPDATE_MY_TIME_LOG(id),
        body,
        {},
        {},
        {
          skipToast: true,
          suppressGlobalErrorModal: true,
        }
      );
      return response?.data ?? response;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update time log"));
    }
  }
);

export const deleteMyTimeLog = createAsyncThunk(
  "timeEntries/deleteMyTimeLog",
  async (id, { rejectWithValue }) => {
    try {
      await del(API_ENDPOINTS.TIME_ENTRIES.DELETE_MY_TIME_LOG(id));
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete time log"));
    }
  }
);

export const fetchTeamTimeSummary = createAsyncThunk(
  "timeEntries/fetchTeamSummary",
  async (params = {}, { rejectWithValue }) => {
    try {
      const finalParams = buildTeamOrProjectSummaryParams(params);
      const response = await get(API_ENDPOINTS.TIME_ENTRIES.GET_TEAM_SUMMARY, finalParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch team time summary"));
    }
  }
);

export const fetchProjectTimeSummary = createAsyncThunk(
  "timeEntries/fetchProjectSummary",
  async (params = {}, { rejectWithValue }) => {
    try {
      const merged = {
        includeDetails: params.includeDetails ?? true,
        ...params,
      };
      const finalParams = buildTeamOrProjectSummaryParams(merged);
      const response = await get(API_ENDPOINTS.TIME_ENTRIES.GET_PROJECT_SUMMARY, finalParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch project time summary"));
    }
  }
);

/** GET /time-logs/developer-summary — "Show Developer Time Entry" table on Team Time Entry tab. */
export const fetchDeveloperTimeSummary = createAsyncThunk(
  "timeEntries/fetchDeveloperSummary",
  async (params = {}, { rejectWithValue }) => {
    try {
      const finalParams = buildTeamOrProjectSummaryParams(params);
      const response = await get(API_ENDPOINTS.TIME_ENTRIES.GET_DEVELOPER_SUMMARY, finalParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch developer time summary"));
    }
  }
);
